/**
 * Twin equipment-state resource — typed wrappers over
 * `/v1/twin/equipment/{id}/state` and `/v1/twin/equipment/backfill-state`.
 *
 * An equipment's state timeline IS its ordered witnessed events (migration 0136,
 * equipment_state). `state` reads the ordered transitions + the current state;
 * `backfillState` derives the timelines from an org's existing heat_events (the
 * sim's generated heats never touched the live writer), idempotently.
 *
 * Mirrors twin-import.ts / twin-entities.ts: a plain resource object over
 * `api.get` / `api.post`.
 */

import { api } from "@/lib/api-client";

/** The honest signal-source taxonomy of a state transition (N3). */
export type EquipmentSignalSource =
  | "coil_register"
  | "analog"
  | "weigh_count"
  | "instrument"
  | "human"
  | "derived"
  | "unconfirmed"
  | "unset";

/**
 * How a transition's to_state was resolved (PL-1): from the entity's declared operating_modes
 * (the precise path), the coarse global map for un-provisioned equipment, a genuinely
 * guarded/ambiguous event, or a value written by the reconcile path. Orthogonal to data_class.
 */
export type EquipmentResolutionBasis =
  | "entity_view"
  | "global_fallback"
  | "guarded_unresolved"
  | "reconciled";

/** One state transition on the wire. */
export interface EquipmentStateRow {
  id: number;
  entity_id: string;
  /** null = a transition recorded outside any heat (the N4 primitive). */
  heat_id: string | null;
  from_state: string | null;
  to_state: string;
  event_kind: string;
  source: EquipmentSignalSource;
  observed_at: string;
  data_class: string;
  caused_by_event_id: number | null;
  resolution_basis: EquipmentResolutionBasis;
}

/** The equipment-state read: the ordered timeline + the current (latest) state. */
export interface EquipmentStateResponse {
  entity_id: string;
  /** null = nothing captured yet (an honest empty state, never fabricated). */
  current: EquipmentStateRow | null;
  timeline: EquipmentStateRow[];
}

export const twinEquipment = {
  state: (entityId: string) =>
    api.get<EquipmentStateResponse>(`/v1/twin/equipment/${entityId}/state`),
  backfillState: () =>
    api.post<{ written: number }>(`/v1/twin/equipment/backfill-state`, {}),
};
